import { useCallback, useContext, useMemo, useRef, useState } from 'react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import isProd from '@utils/isProd';

import {
  GuardedLoadSubmitOptions,
  LoadBuildingGuardrailContext,
  LoadBuildingGuardrailCustomer,
  noopLoadBuildingGuardrail,
} from 'contexts/loadBuildingGuardrail';
import { useToast } from 'hooks/useToaster';
import { setCustomerIsTest } from 'lib/api/setCustomerIsTest';
import {
  LoadBuildingGuardrailAlreadyNotified,
  LoadBuildingGuardrailCancelled,
  toGuardrailCustomer,
} from 'lib/loadBuilding/guardrail';
import {
  ProdTMSGuardrailChoice,
  ProdTMSGuardrailDialog,
} from 'pages/QuoteView/LoadBuilding/ProdTMSGuardrail';
import { Customer, TMSCustomer } from 'types/Load';

export type { GuardedLoadSubmitOptions, LoadBuildingGuardrailCustomer };

function sessionTestCustomerKey(
  customer?: LoadBuildingGuardrailCustomer
): string | undefined {
  if (!customer) {
    return undefined;
  }
  if (customer.id != null) {
    return `id:${customer.id}`;
  }
  if (customer.externalTMSID) {
    return `ext:${customer.externalTMSID}`;
  }
  return undefined;
}

function withSessionTestFlag(
  customer: LoadBuildingGuardrailCustomer | undefined,
  sessionTestCustomerKeys: Set<string>
): LoadBuildingGuardrailCustomer | undefined {
  if (!customer) {
    return undefined;
  }
  const key = sessionTestCustomerKey(customer);
  if (key && sessionTestCustomerKeys.has(key)) {
    return { ...customer, isTest: true };
  }
  return customer;
}

function rememberSessionTestCustomer(
  customer: LoadBuildingGuardrailCustomer | undefined,
  sessionTestCustomerKeys: Set<string>
) {
  const key = sessionTestCustomerKey(customer);
  if (key) {
    sessionTestCustomerKeys.add(key);
  }
}

export function useLoadBuildingGuardrail() {
  const ctx = useContext(LoadBuildingGuardrailContext);
  return ctx ?? noopLoadBuildingGuardrail;
}

type ProviderProps = {
  children: React.ReactNode;
  isTestingEnvironment: boolean;
  tmsName?: string;
};

export function LoadBuildingGuardrailProvider({
  children,
  isTestingEnvironment,
  tmsName,
}: ProviderProps) {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingCustomer, setPendingCustomer] =
    useState<LoadBuildingGuardrailCustomer>();
  const pendingSubmitRef = useRef<
    ((opts: GuardedLoadSubmitOptions) => Promise<void>) | null
  >(null);
  const dialogResolverRef = useRef<{
    resolve: () => void;
    reject: (err: unknown) => void;
  } | null>(null);
  const isHandlingConfirmRef = useRef(false);
  const sessionTestCustomerKeysRef = useRef(new Set<string>());

  const showBanner = useMemo(
    () => !isProd() && !isTestingEnvironment,
    [isTestingEnvironment]
  );

  const needsGuardrail = useCallback(
    (customer?: LoadBuildingGuardrailCustomer) => {
      if (isProd() || isTestingEnvironment) {
        return false;
      }
      if (customer?.isTest) {
        return false;
      }
      return true;
    },
    [isTestingEnvironment]
  );

  const releaseSubmitWaiter = useCallback((err?: unknown) => {
    const resolver = dialogResolverRef.current;
    dialogResolverRef.current = null;
    if (resolver) {
      if (err) {
        resolver.reject(err);
      } else {
        resolver.resolve();
      }
    }
  }, []);

  const finishDialog = useCallback(
    (err?: unknown) => {
      pendingSubmitRef.current = null;
      releaseSubmitWaiter(err);
    },
    [releaseSubmitWaiter]
  );

  const rejectPendingDialog = useCallback((err: unknown) => {
    pendingSubmitRef.current = null;
    const resolver = dialogResolverRef.current;
    dialogResolverRef.current = null;
    if (resolver) {
      resolver.reject(err);
    }
  }, []);

  const cancelDialog = useCallback(() => {
    setPendingCustomer(undefined);
    setDialogOpen(false);
    finishDialog(new LoadBuildingGuardrailCancelled());
  }, [finishDialog]);

  const requestSubmit = useCallback(
    (
      customer:
        Customer | TMSCustomer | LoadBuildingGuardrailCustomer | undefined,
      submit: (opts: GuardedLoadSubmitOptions) => Promise<void>
    ): Promise<void> => {
      const guardCustomer = withSessionTestFlag(
        toGuardrailCustomer(customer),
        sessionTestCustomerKeysRef.current
      );
      if (!needsGuardrail(guardCustomer)) {
        return submit({});
      }

      return new Promise<void>((resolve, reject) => {
        // A second Build click while the dialog is open must not orphan the first promise.
        if (dialogResolverRef.current) {
          rejectPendingDialog(new LoadBuildingGuardrailCancelled());
        }

        setPendingCustomer(guardCustomer);
        pendingSubmitRef.current = submit;
        dialogResolverRef.current = { resolve, reject };
        setDialogOpen(true);
      });
    },
    [needsGuardrail, rejectPendingDialog]
  );

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setDialogOpen(true);
        return;
      }
      if (!isHandlingConfirmRef.current && pendingSubmitRef.current) {
        cancelDialog();
      }
    },
    [cancelDialog]
  );

  const isUnrecoverableConfirmError = (err: unknown) =>
    err instanceof Error &&
    err.message.includes('select a synced customer with a database id');

  const handleDialogConfirm = useCallback(
    async (choice: ProdTMSGuardrailChoice) => {
      const submit = pendingSubmitRef.current;
      if (!submit) {
        setDialogOpen(false);
        setPendingCustomer(undefined);
        return;
      }

      if (choice === 'cancel') {
        cancelDialog();
        return;
      }

      isHandlingConfirmRef.current = true;
      let dialogClosedForSubmit = false;
      try {
        if (choice === 'mark_test_customer') {
          const customerID = pendingCustomer?.id;
          if (!customerID) {
            throw new Error(
              'Cannot mark customer as test: select a synced customer with a database id.'
            );
          }
          const result = await setCustomerIsTest(customerID, true);
          if (result.isErr()) {
            throw new Error(result.error.message);
          }
          const markedTestCustomer =
            toGuardrailCustomer(result.value) ??
            (pendingCustomer
              ? { ...pendingCustomer, isTest: true }
              : undefined);
          rememberSessionTestCustomer(
            markedTestCustomer,
            sessionTestCustomerKeysRef.current
          );
          setPendingCustomer(markedTestCustomer);
          setDialogOpen(false);
          dialogClosedForSubmit = true;
          await submit({});
          finishDialog();
          return;
        }

        setDialogOpen(false);
        dialogClosedForSubmit = true;
        await submit({ acknowledgeProdTMSOverride: true });
        finishDialog();
      } catch (err) {
        if (dialogClosedForSubmit) {
          // createLoad threw after confirm; let forms handle err() + toasts/analytics.
          setDialogOpen(false);
          setPendingCustomer(undefined);
          finishDialog(err);
          return;
        }

        const message =
          err instanceof Error ? err.message : 'Unable to continue.';
        toast({
          description: message,
          variant: 'destructive',
        });
        // Keep dialog, submit callback, and outer requestSubmit promise pending so a
        // successful retry still resolves guarded create-load with the API result.
        if (!isUnrecoverableConfirmError(err) && pendingSubmitRef.current) {
          return;
        }
        setDialogOpen(false);
        setPendingCustomer(undefined);
        finishDialog(new LoadBuildingGuardrailAlreadyNotified(message));
      } finally {
        isHandlingConfirmRef.current = false;
      }
    },
    [
      cancelDialog,
      finishDialog,
      pendingCustomer?.id,
      releaseSubmitWaiter,
      toast,
    ]
  );

  const value = useMemo(
    () => ({
      requestSubmit,
      showBanner,
      isTestingEnvironment,
      tmsName,
    }),
    [requestSubmit, showBanner, isTestingEnvironment, tmsName]
  );

  return (
    <LoadBuildingGuardrailContext.Provider value={value}>
      {children}
      <ProdTMSGuardrailDialog
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
        customerName={pendingCustomer?.name}
        tmsName={tmsName}
        onConfirm={handleDialogConfirm}
      />
    </LoadBuildingGuardrailContext.Provider>
  );
}
